import { useCallback, useEffect, useRef, useState } from 'react';

/* Bejelentkezési rate limit — kliens-oldal (D-10, 2026-09-05).
 *
 * A szerver (`core/login_throttle.py`) fiókonként késleltet (0→1→2→4→8→10 mp) és a
 * 10. hibás próba után Cloudflare Turnstile-t kér. A gépi jelzés a FEJLÉCEKBEN
 * van, a `detail` string marad (`.claude/rules/munkamenet-auth.md` §4):
 *   Retry-After            – ennyi mp-ig 429-et adna a következő próba
 *   X-Turnstile-Required   – 1, ha a következő próbához Turnstile-token kell
 *   X-Turnstile-Site-Key   – a widget publikus site key-e
 * A várakozás 429, a Turnstile-igény 403 — SOHA nem 401, mert arra az axios-
 * interceptor néma /refresh-t és /login-átirányítást futtat.
 *
 * ⚠ IP-tiltás és fiók-zárolás NINCS — ezért a felület sem mond „zárolva" szót:
 * a felhasználó mindig tudja, mikor és hogyan próbálhat újra.
 */

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
let turnstileLoading = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoading) {
    turnstileLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${TURNSTILE_SRC}?render=explicit`;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => { turnstileLoading = null; reject(new Error('turnstile script')); };
      document.head.appendChild(s);
    });
  }
  return turnstileLoading;
}

/** A Turnstile widget. `onToken(token|null)` — a token a következő próbához megy. */
export function TurnstileWidget({ siteKey, onToken }) {
  const ref = useRef(null);
  const widgetId = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !ref.current || !ts) return;
      widgetId.current = ts.render(ref.current, {
        sitekey: siteKey,
        language: 'hu',
        callback: (token) => onToken?.(token),
        'expired-callback': () => onToken?.(null),
        'error-callback': () => { onToken?.(null); setFailed(true); },
      });
    }).catch(() => setFailed(true));
    return () => {
      cancelled = true;
      try { if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current); }
      catch { /* no-op */ }
    };
  }, [siteKey, onToken]);

  return (
    <div>
      <div ref={ref} style={{ display: 'flex', justifyContent: 'center' }} />
      {failed && (
        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0.4rem 0 0' }}>
          Az ellenőrző elem nem töltődött be. Frissítsd az oldalt, vagy próbáld újra később.
        </p>
      )}
    </div>
  );
}

/**
 * A throttle-állapot egy jelszavas űrlaphoz: visszaszámláló + Turnstile-igény.
 * Használat: `const t = useLoginThrottle(); ... catch (err) { t.absorb(err) }`,
 * a küldéskor `t.turnstileToken`, a gombhoz `t.blocked`.
 */
export function useLoginThrottle() {
  const [retryAt, setRetryAt] = useState(null);         // ms timestamp
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [turnstile, setTurnstile] = useState(null);     // { siteKey } | null
  const [turnstileToken, setTurnstileToken] = useState(null);

  useEffect(() => {
    if (!retryAt) { setSecondsLeft(0); return undefined; }
    const tick = () => {
      const left = Math.ceil((retryAt - Date.now()) / 1000);
      if (left <= 0) { setSecondsLeft(0); setRetryAt(null); }
      else setSecondsLeft(left);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [retryAt]);

  /** A hibaválasz fejléceit dolgozza fel; a megjelenítendő üzenetet adja vissza. */
  const absorb = useCallback((err, fallback) => {
    const resp = err?.response;
    const headers = resp?.headers || {};
    const retryAfter = parseInt(headers['retry-after'], 10);
    if (retryAfter > 0) setRetryAt(Date.now() + retryAfter * 1000);
    if (headers['x-turnstile-required'] === '1' && headers['x-turnstile-site-key']) {
      setTurnstile({ siteKey: headers['x-turnstile-site-key'] });
      setTurnstileToken(null);   // egy token egyszer használható
    }
    return resp?.data?.detail || fallback;
  }, []);

  const reset = useCallback(() => {
    setRetryAt(null); setTurnstile(null); setTurnstileToken(null);
  }, []);

  const onToken = useCallback((token) => setTurnstileToken(token), []);

  return {
    secondsLeft,
    blocked: secondsLeft > 0 || (!!turnstile && !turnstileToken),
    turnstile,
    turnstileToken,
    onToken,
    absorb,
    reset,
  };
}

/** A visszaszámláló sora — csak akkor renderel, ha van mit. */
export function ThrottleHint({ throttle }) {
  if (!throttle.secondsLeft) return null;
  return (
    <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: 0, textAlign: 'center' }}>
      Újra próbálkozhatsz {throttle.secondsLeft} másodperc múlva.
    </p>
  );
}
